/* @jsx h */
import { computed, defineComponent, h, watch } from "vue";
import {
  Dropdown,
  supportedLanguages,
  useLocalization,
} from "@lemmonhq/uikit";
import { useBranding } from "../../providers/Branding";

const isGeorgia = (country?: string) => {
  const normalized = country?.trim().toLowerCase();
  return (
    normalized === "georgia" || normalized === "ge" || normalized === "geo"
  );
};

export const getCustomerLanguageIds = ({
  brandId,
  country,
  languageIds,
}: {
  brandId?: string;
  country?: string;
  languageIds?: string[];
}) => {
  if (languageIds?.length) return languageIds;
  if (brandId === "javshani") return ["ka", "en"];
  if (isGeorgia(country)) return ["en", "ka"];
  return ["fr", "de", "it", "en"];
};

export const CustomerLanguageSelector = defineComponent({
  name: "CustomerLanguageSelector",
  props: {
    brandId: {
      type: String,
      default: "",
    },
    country: {
      type: String,
      default: "",
    },
    languageIds: {
      type: Array as () => string[],
      default: undefined,
    },
    variant: {
      type: String as () => "default" | "overlay" | "compact",
      default: "default",
    },
  },
  setup(props) {
    const { t, locale, setLocale } = useLocalization();
    const { branding } = useBranding();

    /* Fall back to the host's branding so a call site that forgets `brandId`
       (every restaurant route did) cannot drop Javshani onto the Swiss list. */
    const brandId = computed(() => props.brandId || branding.value?.id);

    const selectableLanguages = computed(() => {
      return getCustomerLanguageIds({
        brandId: brandId.value,
        country: props.country,
        languageIds: props.languageIds,
      })
        .map((id) => supportedLanguages.find((language) => language.id === id))
        .filter((language): language is (typeof supportedLanguages)[number] =>
          Boolean(language),
        );
    });

    watch(
      [selectableLanguages, locale],
      ([languages]) => {
        if (!languages.length) return;
        if (languages.some((language) => language.id === locale.value)) return;
        /* Branding loads async; until it resolves the list is still the
           default one, so coercing here would stomp a valid stored locale. */
        if (!props.brandId && !props.languageIds && !branding.value) return;
        setLocale(languages[0].id);
      },
      { immediate: true },
    );

    const activeLanguage = computed(
      () =>
        selectableLanguages.value.find(
          (language) => language.id === locale.value,
        ) ?? selectableLanguages.value[0],
    );

    const triggerClass = computed(() => {
      if (props.variant === "overlay") {
        return "inline-flex items-center gap-1.5 rounded-lg border border-white/30 bg-black/20 px-2.5 py-1.5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-black/30 focus:outline-none focus:ring-2 focus:ring-white/50";
      }
      if (props.variant === "compact") {
        return "inline-flex h-9 items-center gap-1 rounded-full bg-base-200/70 pl-2.5 pr-2 text-xs font-semibold text-base-content/70 transition-colors hover:bg-base-200 hover:text-base-content focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";
      }
      return "inline-flex items-center gap-1.5 rounded-lg border border-base-300 bg-base-100 px-2.5 py-1.5 text-sm font-medium text-base-content transition-colors hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-primary";
    });

    return () => (
      <Dropdown
        placement="bottom-end"
        offset={6}
        closeOnContentClick
        contentClass="min-w-[10rem] max-w-[calc(100vw-1rem)] rounded-xl border border-base-300 bg-base-100 p-1.5 shadow-xl"
      >
        {{
          trigger: () => (
            <button
              type="button"
              class={triggerClass.value}
              aria-label={t("customer.home.select_language")}
            >
              <span
                class="material-symbols-outlined text-base"
                aria-hidden="true"
              >
                language
              </span>
              <span class="uppercase">
                {activeLanguage.value?.id ?? locale.value}
              </span>
              <span
                class="material-symbols-outlined text-base opacity-70"
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>
          ),
          default: () => (
            <>
              <span class="sr-only">
                {t("customer.home.select_language")}
              </span>
              {selectableLanguages.value.map((language) => {
                const active = language.id === locale.value;
                return (
                  <button
                    key={language.id}
                    type="button"
                    class={[
                      "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                      active
                        ? "bg-primary/10 font-semibold text-primary"
                        : "text-base-content hover:bg-base-200",
                    ]}
                    aria-current={active ? "true" : undefined}
                    onClick={() => setLocale(language.id)}
                  >
                    <span>{language.name}</span>
                    {active && (
                      <span
                        class="material-symbols-outlined text-base"
                        aria-hidden="true"
                      >
                        check
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          ),
        }}
      </Dropdown>
    );
  },
});
